import { Platform } from 'react-native';
import { TextInputRef } from 'shared/components/form/text-input';
import { useTranslation } from 'react-i18next';
import { useBreeds } from 'shared/state/breeds';
import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { Animal, Breed } from 'shared/@generated/models';
import { useBreedSelectionHistory } from 'shared/library/use-breed-selection-history';
import { tryFocus } from 'shared/library/try-focus';
import { ConfirmModal, ConfirmModalRef } from 'shared/components/confirm-modal';
import { FullscreenModal } from 'shared/components/fullscreen-modal';
import { BaseModalRef } from 'shared/components/base-modal';
import { BreedSelectionField } from 'shared/components/form/breed-selection-field';
import { BreedPickerPanel } from 'shared/components/breed-picker-panel';

export type BreedInputValues = (string | number)[];

const EMPTY_BREEDS: Breed[] = [];

type BreedSuggestion = {
  breed: Breed;
  query: string;
};

function normalizeBreedValue(value: string) {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
}

function damerauLevenshteinDistance(a: string, b: string) {
  const matrix = Array.from({ length: a.length + 1 }, () => Array(b.length + 1).fill(0));

  for (let row = 0; row <= a.length; row++) {
    matrix[row]![0] = row;
  }

  for (let column = 0; column <= b.length; column++) {
    matrix[0]![column] = column;
  }

  for (let row = 1; row <= a.length; row++) {
    for (let column = 1; column <= b.length; column++) {
      matrix[row]![column] =
        a[row - 1] === b[column - 1]
          ? matrix[row - 1]![column - 1]!
          : Math.min(
              matrix[row - 1]![column]! + 1,
              matrix[row]![column - 1]! + 1,
              matrix[row - 1]![column - 1]! + 1,
            );

      if (row > 1 && column > 1 && a[row - 1] === b[column - 2] && a[row - 2] === b[column - 1]) {
        matrix[row]![column] = Math.min(matrix[row]![column]!, matrix[row - 2]![column - 2]! + 1);
      }
    }
  }

  return matrix[a.length]![b.length]!;
}

function findCloseBreedMatch(query: string, breeds: Breed[]) {
  const normalizedQuery = normalizeBreedValue(query);

  if (!normalizedQuery) {
    return null;
  }

  const normalizedBreeds = breeds.map((breed) => ({
    breed,
    normalizedName: normalizeBreedValue(breed.name),
  }));

  const exactMatches = normalizedBreeds.filter(
    ({ normalizedName }) => normalizedName === normalizedQuery,
  );

  if (exactMatches.length === 1) {
    return exactMatches[0]!.breed;
  }

  if (normalizedQuery.length < 5) {
    return null;
  }

  const rankedMatches = normalizedBreeds
    .map(({ breed, normalizedName }) => {
      const maxLength = Math.max(normalizedName.length, normalizedQuery.length);
      const similarity =
        maxLength > 0
          ? 1 - damerauLevenshteinDistance(normalizedName, normalizedQuery) / maxLength
          : 0;

      return { breed, similarity };
    })
    .sort((a, b) => b.similarity - a.similarity);

  const [bestMatch, secondBestMatch] = rankedMatches;

  if (!bestMatch || bestMatch.similarity < 0.88) {
    return null;
  }

  if (secondBestMatch && bestMatch.similarity - secondBestMatch.similarity < 0.08) {
    return null;
  }

  return bestMatch.breed;
}

const BreedInput = ({
  value,
  onSelectBreed,
  onSelectQuery,
  onChange,
  dense,
  historyEnabled = true,
  allowQuery = true,
  confirmCloseBreedMatch = false,
  animal,
  initialBreeds = EMPTY_BREEDS,
  placeholder,
  triggerPlaceholder,
  trigger,
}: {
  value?: BreedInputValues | null;
  onChange?: (value: BreedInputValues) => void;
  onSelectBreed?: (breed: Breed) => void;
  onSelectQuery?: (value: string) => void;
  dense?: boolean;
  historyEnabled?: boolean;
  allowQuery?: boolean;
  confirmCloseBreedMatch?: boolean;
  animal?: Animal;
  initialBreeds?: Breed[];
  placeholder?: string;
  triggerPlaceholder?: string;
  trigger?: (modal: BaseModalRef) => ReactNode;
}) => {
  const { t } = useTranslation('components');
  const {
    breeds: allBreeds,
    error: breedsError,
    fetchBreeds,
    loading: breedsLoading,
  } = useBreeds();
  const isNativeModal = Platform.OS !== 'web';
  const [query, setQuery] = useState('');
  const [pendingBreedSuggestion, setPendingBreedSuggestion] = useState<BreedSuggestion | null>(
    null,
  );
  const inputRef = useRef<TextInputRef>(null);
  const modalRef = useRef<BaseModalRef>(null);
  const confirmModalRef = useRef<ConfirmModalRef>(null);
  const focusTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { remember } = useBreedSelectionHistory();

  const allAvailableBreeds = useMemo(() => {
    const breedsById = new Map<number, Breed>();

    for (const breed of initialBreeds) {
      breedsById.set(breed.id, breed);
    }

    for (const breed of allBreeds) {
      breedsById.set(breed.id, breed);
    }

    return Array.from(breedsById.values());
  }, [allBreeds, initialBreeds]);

  const breeds = useMemo(() => {
    return animal
      ? allAvailableBreeds.filter((breed) => breed.animal === animal)
      : allAvailableBreeds;
  }, [animal, allAvailableBreeds]);

  const normalizedQuery = normalizeBreedValue(query);
  const filteredBreeds = breeds
    .filter((breed) => normalizeBreedValue(breed.name).includes(normalizedQuery))
    .sort((a, b) => a.name.localeCompare(b.name));

  useEffect(() => {
    return () => {
      if (focusTimeoutRef.current) {
        clearTimeout(focusTimeoutRef.current);
      }
    };
  }, []);

  function selectBreed(breed: Breed) {
    setPendingBreedSuggestion(null);
    onSelectBreed?.(breed);
    onSelect(breed.id);
    setQuery('');
  }

  function selectQuery(query: string) {
    setPendingBreedSuggestion(null);
    onSelectQuery?.(query);
    onSelect(query);
    setQuery('');
  }

  function openModal(modal: BaseModalRef) {
    setPendingBreedSuggestion(null);
    setQuery('');
    modal.open();

    requestAnimationFrame(() => {
      if (focusTimeoutRef.current) {
        clearTimeout(focusTimeoutRef.current);
      }

      focusTimeoutRef.current = setTimeout(() => {
        tryFocus(inputRef);
      }, 160);
    });
  }

  function onChangeQuery(nextQuery: string) {
    setPendingBreedSuggestion(null);
    setQuery(nextQuery);
  }

  function trySelectQuery(nextQuery: string) {
    const trimmedQuery = nextQuery.trim();

    if (!trimmedQuery) {
      modalRef.current?.close();
      return;
    }

    if (confirmCloseBreedMatch) {
      const suggestedBreed = findCloseBreedMatch(trimmedQuery, breeds);

      if (suggestedBreed) {
        setPendingBreedSuggestion({ breed: suggestedBreed, query: trimmedQuery });
        confirmModalRef.current?.open();
        return;
      }
    }

    selectQuery(trimmedQuery);
  }

  function onSelect(newValue: string | number) {
    modalRef.current?.close();
    remember(newValue);
    onChange?.([newValue]);
  }

  function remove(oldVal: string | number) {
    onChange?.(value ? value.filter((val) => val !== oldVal) : []);
  }

  return (
    <>
      <FullscreenModal
        ref={modalRef}
        headerTitle={t('form.breed_input.choose_breed')}
        centered={!isNativeModal}
        className={'md:h-[min(40rem,96dvh)]'}
        scrollable={false}
        contentTestID={'breed-input-modal'}
        onClose={() => {
          if (focusTimeoutRef.current) {
            clearTimeout(focusTimeoutRef.current);
          }
        }}
        trigger={(modal) =>
          trigger ? (
            trigger({ ...modal, open: () => openModal(modal) })
          ) : (
            <BreedSelectionField
              values={value?.slice(0, 1) ?? []}
              breeds={allAvailableBreeds}
              placeholder={triggerPlaceholder ?? placeholder ?? t('form.breed_input.placeholder')}
              accessibilityLabel={t('form.breed_input.choose_breed')}
              onOpen={() => openModal(modal)}
              onRemove={remove}
              dense={dense}
              testID={'breed-input-trigger'}
              itemTestID={(item) =>
                typeof item === 'number'
                  ? 'breed-input-selected-breed'
                  : 'breed-input-selected-query'
              }
            />
          )
        }
      >
        <BreedPickerPanel
          query={query}
          setQuery={onChangeQuery}
          breeds={filteredBreeds}
          allBreeds={allAvailableBreeds}
          loading={breedsLoading}
          error={breedsError && allAvailableBreeds.length === 0}
          onRetry={() => void fetchBreeds()}
          selected={value?.slice(0, 1) ?? []}
          multiple={false}
          allowQuery={allowQuery}
          recordHistory={false}
          onToggle={selectBreed}
          onRemove={remove}
          onSelectQuery={trySelectQuery}
          getQueryLabel={(query) =>
            t(
              historyEnabled
                ? 'form.breed_input.view_all_matchning_query'
                : 'form.breed_input.select_query',
              { query },
            )
          }
          placeholder={placeholder}
          autoFocus={!isNativeModal}
          searchInputRef={inputRef}
          inputTestID={'breed-input-text'}
          queryOptionTestID={'breed-input-query-option'}
          retryTestID={'breed-input-retry'}
          optionTestID={(breed) => {
            const slug = (breed.slug.en || breed.name)
              .toLowerCase()
              .replace(/[^a-z0-9]+/g, '-')
              .replace(/^-|-$/g, '');

            return `breed-input-option-${slug}`;
          }}
        />
      </FullscreenModal>

      <ConfirmModal
        ref={confirmModalRef}
        title={t('form.breed_input.confirm_matching_breed_title')}
        description={
          pendingBreedSuggestion
            ? t('form.breed_input.confirm_matching_breed_description', {
                breed: pendingBreedSuggestion.breed.name,
              })
            : undefined
        }
        confirmLabel={t('form.breed_input.select_suggested_breed')}
        cancelLabel={t('form.breed_input.continue_as_mixed_breed')}
        onConfirm={() => {
          if (!pendingBreedSuggestion) {
            return Promise.resolve();
          }

          selectBreed(pendingBreedSuggestion.breed);
          return Promise.resolve();
        }}
        onCancel={() => {
          if (!pendingBreedSuggestion) {
            return;
          }

          selectQuery(pendingBreedSuggestion.query);
        }}
      />
    </>
  );
};

export { BreedInput };
